import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import LanguageIconBW from "@/assets/language_bw.svg";
import LanguageIcon from "@/assets/language.svg";

import useMapStore from "@/store/mapStore";
import { t } from "i18next";
import { useEffect, useState } from "react";

function LanguageToggle() {
  const [isOpen, setIsOpen] = useState(false);
  const setCurrentLang = useMapStore((state) => state.setCurrentLang);
  const currentLang = useMapStore((state) => state.currentLang);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const haveCustomPosition = useMapStore((state) => state.haveCustomPosition);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);
  const languages = [
    {
      value: "en",
      title: "English",
      countryCode: "US",
      abb: "ENG",
    },
    {
      value: "ar",
      title: "Arabic",
      countryCode: "AE",
      abb: "العربية",
    },
    {
      value: "ru",
      title: "Russian",
      countryCode: "RU",
      abb: "РУС",
    },
    {
      value: "zh",
      title: "Chinese",
      countryCode: "CN",
      abb: "中文",
    },
  ];

  useEffect(() => {
    if (isOpen) {
      // Pushing the change to the end of the call stack
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 0);

      return () => clearTimeout(timer);
    } else {
      document.body.style.pointerEvents = "auto";
    }
  }, [isOpen]);

  if (mobileMode || verticalKioskMode) {
    return (
      <Accordion type="single" collapsible>
        <AccordionItem value="item-1" className="border-0">
          <AccordionTrigger
            className={cn(
              "flex h-8 cursor-pointer items-center justify-start gap-3 py-2 hover:no-underline",
            )}
          >
            <img src={LanguageIconBW} alt="Language" className="ml-1 h-5 w-5" />
            <span className="font-normal capitalize group-data-[dir=ltr]:mr-auto group-data-[dir=rtl]:ml-auto">
              {t("language-settings")}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <RadioGroup
              defaultValue={currentLang}
              onValueChange={setCurrentLang}
              dir={currentLang === "ar" ? "rtl" : "ltr"}
            >
              {languages.map((language, idx) => (
                <div className="flex items-center" key={idx}>
                  <RadioGroupItem
                    value={language.value}
                    id={language.value}
                    className="h-5 w-5 border-foreground text-foreground"
                  />
                  <Label
                    htmlFor={language.value}
                    className="cursor-pointer text-sm font-normal group-data-[dir=ltr]:ml-4 group-data-[dir=rtl]:mr-4"
                  >
                    {language.title}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <DropdownMenu
      dir={currentLang === "ar" ? "rtl" : "ltr"}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenuTrigger asChild>
        <div
          className={cn(
            "absolute top-10 z-[6] cursor-pointer rounded-full bg-white p-[11px] text-lg text-primary shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)] hover:text-black hover:shadow-[0px_1px_6px_3px_rgba(0,0,0,0.7)] group-data-[dir=ltr]:right-[152px] group-data-[dir=rtl]:left-[152px] kiosk:bottom-[calc(28%+110px)] kiosk:top-auto kiosk:p-5 kiosk:group-data-[dir=ltr]:right-16 kiosk:group-data-[dir=rtl]:left-16",
            !kioskMode &&
              !haveCustomPosition &&
              "group-data-[dir=ltr]:right-[38px] group-data-[dir=rtl]:left-[38px]",
          )}
        >
          <img
            src={LanguageIcon}
            alt="Language"
            className="h-5 w-5 kiosk:h-8 kiosk:w-8"
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="z-[200] w-auto min-w-0 border-0 bg-transparent px-2 py-2 shadow-none outline-none group-data-[dir=ltr]:-mr-2 group-data-[dir=rtl]:-ml-2"
        align={verticalKioskMode ? "center" : "end"}
        side={
          verticalKioskMode
            ? currentLang === "ar"
              ? "right"
              : "left"
            : "bottom"
        }
      >
        {languages.map((language, idx) => (
          <DropdownMenuItem
            key={idx}
            onClick={() => setCurrentLang(language.value)}
            className={cn(
              "mb-3 flex cursor-pointer items-center justify-center rounded-xl px-6 py-[3px] text-lg font-light text-primary shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)] hover:shadow-[0px_1px_6px_3px_rgba(0,0,0,0.7)]",
              currentLang === language.value
                ? "bg-primary text-white hover:bg-primary focus:bg-primary focus:text-white"
                : "bg-white hover:bg-white focus:bg-white focus:text-primary",
            )}
          >
            {t(language.abb)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageToggle;
